import React, { useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import introJs from 'intro.js';
import 'intro.js/introjs.css';
import { Question } from '@phosphor-icons/react';
import { useAuth } from '../context/AuthContext';

const TOUR_STORAGE_KEY = 'stockaris_tour_seen_v1';

const TOUR_STEPS = [
  {
    element: '[data-tour="dashboard"]',
    intro:
      '<b>Welcome to Stockaris WMS!</b><br/>Your Dashboard shows the Warehouse Health Score, KPIs, and a live event feed powered by WebSockets.',
    position: 'right',
  },
  {
    element: '[data-tour="orders"]',
    intro:
      '<b>Orders</b><br/>Create or import orders from Shopify / Etsy / Amazon (mocked). Click any order to see its timeline of picks, packs, labels and shipments.',
    position: 'right',
  },
  {
    element: '[data-tour="picking"]',
    intro:
      '<b>Picking</b><br/>Mobile-optimized flow with camera barcode scanning, FIFO batch prompts, voice-pick TTS, and route-optimized walks.',
    position: 'right',
  },
  {
    element: '[data-tour="shipping"]',
    intro:
      '<b>Shipping</b><br/>Generate PDF or ZPL labels, select mock carriers (Sendle, AusPost), and bulk-print labels as a signed ZIP.',
    position: 'right',
  },
  {
    element: '[data-tour="compliance"]',
    intro:
      '<b>Compliance &amp; Retention</b><br/>Export signed ZIP bundles by date range, manage 7-year retention policies, and audit every mutation. You&rsquo;re all set &mdash; happy shipping!',
    position: 'top',
  },
];

export default function GuidedTour() {
  const { user } = useAuth();
  const location = useLocation();
  const instanceRef = useRef(null);

  const killTour = useCallback(() => {
    // Fully exit any running tour + sweep orphaned intro.js layers.
    // Does NOT persist the seen flag — callers do that when appropriate.
    let wasActive = !!instanceRef.current;
    if (instanceRef.current) {
      try { instanceRef.current.exit(true); } catch (_) { /* noop */ }
      instanceRef.current = null;
    }
    const orphans = document.querySelectorAll(
      '.introjs-overlay, .introjs-helperLayer, .introjs-tooltipReferenceLayer, .introjs-disableInteraction, .stockaris-tour-tooltip'
    );
    if (orphans.length) wasActive = true;
    orphans.forEach((el) => el.remove());
    return wasActive;
  }, []);

  const startTour = useCallback(() => {
    if (window.innerWidth < 768) return;
    const steps = TOUR_STEPS.filter((s) => document.querySelector(s.element));
    if (steps.length === 0) return;
    killTour();
    const tour = introJs.tour();
    tour.setOptions({
      steps,
      showProgress: true,
      showBullets: true,
      exitOnEsc: true,
      exitOnOverlayClick: true,
      disableInteraction: false,
      nextLabel: 'Next →',
      prevLabel: '← Back',
      doneLabel: 'Finish',
      tooltipClass: 'stockaris-tour-tooltip',
      highlightClass: 'stockaris-tour-highlight',
      scrollToElement: true,
      scrollTo: 'element',
    });
    const markDone = () => {
      localStorage.setItem(TOUR_STORAGE_KEY, '1');
      instanceRef.current = null;
      // Belt-and-braces sweep in case intro.js leaves layers
      setTimeout(() => {
        document
          .querySelectorAll('.introjs-overlay, .introjs-helperLayer, .introjs-tooltipReferenceLayer, .introjs-disableInteraction')
          .forEach((el) => el.remove());
      }, 50);
    };
    tour.oncomplete(markDone);
    tour.onexit(markDone);
    instanceRef.current = tour;
    tour.start();
  }, [killTour]);

  useEffect(() => {
    if (!user) return;
    if (user.role !== 'admin') return;
    if (window.innerWidth < 768) return;
    // iter229 — the Competition Demo has its own scenario tour experience;
    // suppress the general Stockaris onboarding tour for demo tenants so
    // judges land on a clean dashboard.
    if (user?.company?.is_demo) return;
    // Server-side flag wins — if the user is already marked onboarded
    // (seeded admins, migrated accounts), don't auto-launch. Local flag
    // remains the fallback for fresh signups that haven't hit /auth/me yet.
    if (user.tour_seen_at) return;
    if (localStorage.getItem(TOUR_STORAGE_KEY)) return;
    const t = setTimeout(() => {
      // Re-check right before launching — a route change may have killed + set the flag
      if (localStorage.getItem(TOUR_STORAGE_KEY)) return;
      startTour();
    }, 1000);
    return () => clearTimeout(t);
  }, [user, startTour]);

  // Listen for programmatic trigger (e.g. from What's New CTA)
  useEffect(() => {
    const handler = () => startTour();
    window.addEventListener('stockaris:start-tour', handler);
    return () => window.removeEventListener('stockaris:start-tour', handler);
  }, [startTour]);

  // Real route changes → cleanly exit the tour if one is running.
  // Uses instanceRef.current to avoid firing on mount (it's null then).
  // If a tour WAS active at nav time, mark it seen so we don't relaunch.
  useEffect(() => {
    if (instanceRef.current) {
      killTour();
      localStorage.setItem(TOUR_STORAGE_KEY, '1');
    }
  }, [location.pathname, killTour]);

  // Global ESC escape hatch — always kills stuck intro.js layers
  useEffect(() => {
    const onKey = (e) => {
      if (e.key === 'Escape') {
        const stuck = document.querySelector('.introjs-overlay, .introjs-helperLayer');
        if (stuck) {
          killTour();
          localStorage.setItem(TOUR_STORAGE_KEY, '1');
        }
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [killTour]);

  useEffect(() => {
    return () => killTour();
  }, [killTour]);

  const handleRestart = () => {
    localStorage.removeItem(TOUR_STORAGE_KEY);
    startTour();
  };

  if (!user) return null;

  return (
    <button
      type="button"
      onClick={handleRestart}
      data-testid="guided-tour-restart-btn"
      aria-label="Take the guided tour"
      className="hidden md:flex fixed bottom-20 right-5 z-30 items-center gap-2 bg-brand hover:bg-brand/90 text-brand-foreground px-4 py-2.5 rounded-md shadow-lg font-semibold text-xs uppercase tracking-wider transition-colors"
    >
      <Question size={16} weight="bold" />
      <span>Take Tour</span>
    </button>
  );
}

// Programmatic trigger for other components
export function openTour() {
  window.dispatchEvent(new CustomEvent('stockaris:start-tour'));
}
